import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  catalogLabels,
  filterByCatalog,
} from "../../../../helpers/catalogFilter.helper";

const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const UpdateRoleDialog = ({
  open,
  user,
  roles = [],
  loading = false,
  rolesLoading = false,
  permissionCatalog = false,
  onClose,
  onSubmit,
}) => {
  const [selectedRoles, setSelectedRoles] = useState([]);

  const roleOptions = useMemo(() => {
    if (!permissionCatalog) return catalogLabels(roles);
    const seen = new Set();
    return (Array.isArray(roles) ? roles : [])
      .map((item) => String(typeof item === "string" ? item : item?.name || "").trim())
      .filter((name) => {
        if (!name || seen.has(name)) return false;
        seen.add(name);
        return true;
      });
  }, [permissionCatalog, roles]);

  useEffect(() => {
    if (!open) return;
    const assigned = Array.isArray(user?.roles) ? user.roles : [];
    if (rolesLoading) {
      setSelectedRoles(assigned.map(String).filter(Boolean));
      return;
    }
    setSelectedRoles(filterByCatalog(assigned, roles));
  }, [open, user, roles, rolesLoading]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Update Role</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Assign roles for{" "}
          <strong>{user?.username || user?.userId || "selected user"}</strong>
        </Typography>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={roleOptions}
          value={selectedRoles}
          loading={rolesLoading}
          isOptionEqualToValue={(option, selected) =>
            String(option) === String(selected)
          }
          onChange={(_, value) =>
            setSelectedRoles(filterByCatalog(value, roles))
          }
          renderOption={(props, option, { selected }) => {
            const { key, ...liProps } = props;
            return (
              <li key={key ?? String(option)} {...liProps}>
                <Checkbox
                  icon={uncheckedIcon}
                  checkedIcon={checkedIcon}
                  checked={selected}
                  size="small"
                  tabIndex={-1}
                  disableRipple
                  sx={{ mr: 1, pointerEvents: "none" }}
                />
                {option}
              </li>
            );
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={option}
                size="small"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Roles"
              placeholder="Select roles"
              helperText={
                rolesLoading
                  ? "Loading roles..."
                  : roleOptions.length
                    ? "Select one or more roles from the current role list."
                    : "No roles available."
              }
            />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={loading || rolesLoading || selectedRoles.length === 0}
          onClick={() => onSubmit(selectedRoles)}
        >
          {loading ? "Saving..." : "Save Roles"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateRoleDialog;
